import { useEffect, useMemo, useState } from "react";
import { Sparkle, FunnelSimple, X, Info, CaretUp, CaretDown, ImageSquare, Fire, Drop, Moon, CurrencyInr, UsersThree, Pulse, WarningCircle } from "@phosphor-icons/react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import AIDrawer, { useAIStream } from "@/components/AIDrawer";
import { api, inr, compact } from "@/lib/api";

const RANK_STYLE = {
  above: { label: "Above avg", cls: "bg-success/10 text-[#00953e] border-success/25" },
  average: { label: "Average", cls: "bg-[#F3F4F6] text-[#525866] border-[#E5E7EB]" },
  below: { label: "Below avg", cls: "bg-red-50 text-red-500 border-red-200" },
};
const rankBand = (r) => {
  if (!r || r === "UNKNOWN") return null;
  if (r.startsWith("ABOVE")) return "above";
  if (r.startsWith("BELOW")) return "below";
  return "average";
};
const RankBadge = ({ r }) => {
  const band = rankBand(r);
  if (!band) return <span className="text-[11px] text-[#c3c7d1]">—</span>;
  const s = RANK_STYLE[band];
  return <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-bold ${s.cls}`}>{s.label}</span>;
};

const VERDICTS = {
  top: { label: "Top performer", icon: Fire, cls: "bg-success/10 text-[#00953e] border-success/25" },
  drain: { label: "Budget drain", icon: Drop, cls: "bg-red-50 text-red-500 border-red-200" },
  fatigued: { label: "Fatigued", icon: Moon, cls: "bg-warning/10 text-[#b07600] border-warning/30" },
  ok: { label: "OK", icon: Pulse, cls: "bg-[#F3F4F6] text-[#8a90a0] border-[#E5E7EB]" },
};

const computeVerdict = (ad, medianCpl) => {
  if (ad.spend >= 1000 && ad.leads === 0) return "drain";
  if (ad.leads >= 3 && ad.cpl > 0 && medianCpl > 0 && ad.cpl <= 0.7 * medianCpl) return "top";
  if ((ad.frequency || 0) >= 3.5 || rankBand(ad.quality_ranking) === "below") return "fatigued";
  return "ok";
};

const issueLabel = (s) => {
  const sl = (s || "").toLowerCase();
  if (sl.includes("development mode")) return "Dev-mode post";
  if (sl.includes("optimisation goal") || sl.includes("optimization goal")) return "Invalid opt. goal";
  return (s || "Error").slice(0, 22);
};

const FSelect = ({ value, onChange, options, all, width = "w-44", testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className={`${width} h-9 text-xs font-semibold bg-white`} data-testid={testId}><SelectValue /></SelectTrigger>
    <SelectContent className="max-h-72">
      <SelectItem value="all">{all}</SelectItem>
      {options.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
    </SelectContent>
  </Select>
);

const Stat = ({ icon: Icon, label, value, sub, accent, testId }) => (
  <div className="bg-white border border-[#E5E7EB] rounded-lg p-4" data-testid={testId}>
    <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[#8a90a0]"><Icon size={13} weight="duotone" className={accent ? "text-[#FF6B35]" : "text-[#0052FF]"} /> {label}</div>
    <div className="mt-1.5 font-heading text-xl font-extrabold tracking-tighter text-[#0A0D14]">{value}</div>
    {sub && <div className="text-[11px] text-[#8a90a0] mt-0.5">{sub}</div>}
  </div>
);

const NUMERIC_COLS = [
  { key: "spend", label: "Spend" }, { key: "impressions", label: "Impr." }, { key: "cpm", label: "CPM" },
  { key: "clicks", label: "Clicks" }, { key: "ctr", label: "CTR" }, { key: "leads", label: "Leads" },
  { key: "cpl", label: "CPL" }, { key: "frequency", label: "Freq" },
];

export default function Ads() {
  const [ads, setAds] = useState(null);
  const [drawer, setDrawer] = useState(null);
  const [preview, setPreview] = useState(null);
  const ai = useAIStream();

  const [fCamp, setFCamp] = useState("all");
  const [fAdset, setFAdset] = useState("all");
  const [fStatus, setFStatus] = useState("all");
  const [fVerdict, setFVerdict] = useState("all");
  const [fIssue, setFIssue] = useState("all");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ key: "spend", dir: "desc" });

  useEffect(() => {
    api.get("/ads").then(({ data }) => setAds(data)).catch(() => setAds([]));
  }, []);

  const medianCpl = useMemo(() => {
    const cpls = (ads || []).filter((a) => a.leads > 0 && a.cpl > 0).map((a) => a.cpl).sort((x, y) => x - y);
    return cpls.length ? cpls[Math.floor(cpls.length / 2)] : 0;
  }, [ads]);

  const withVerdict = useMemo(() => (ads || []).map((a) => ({ ...a, verdict: computeVerdict(a, medianCpl) })), [ads, medianCpl]);

  const campaigns = useMemo(() => [...new Set(withVerdict.map((a) => a.campaign_name).filter(Boolean))].sort(), [withVerdict]);
  const adsets = useMemo(() => [...new Set(withVerdict.filter((a) => fCamp === "all" || a.campaign_name === fCamp).map((a) => a.adset_name).filter(Boolean))].sort(), [withVerdict, fCamp]);

  const filtered = useMemo(() => {
    if (!ads) return null;
    const s = q.toLowerCase();
    return withVerdict.filter((a) =>
      (fCamp === "all" || a.campaign_name === fCamp) &&
      (fAdset === "all" || a.adset_name === fAdset) &&
      (fStatus === "all" || a.status === fStatus) &&
      (fVerdict === "all" || a.verdict === fVerdict) &&
      (fIssue === "all" || (fIssue === "with" ? (a.issues || []).length > 0 : (a.issues || []).length === 0)) &&
      (!s || (a.name || "").toLowerCase().includes(s) || (a.headline || "").toLowerCase().includes(s) || (a.campaign_name || "").toLowerCase().includes(s)));
  }, [ads, withVerdict, fCamp, fAdset, fStatus, fVerdict, fIssue, q]);

  const sorted = useMemo(() => {
    if (!filtered) return null;
    return [...filtered].sort((a, b) => ((a[sort.key] || 0) - (b[sort.key] || 0)) * (sort.dir === "asc" ? 1 : -1));
  }, [filtered, sort]);

  const stats = useMemo(() => {
    const f = filtered || [];
    const spend = f.reduce((s, a) => s + a.spend, 0);
    const leads = f.reduce((s, a) => s + a.leads, 0);
    return {
      total: f.length,
      active: f.filter((a) => a.status === "active").length,
      spend, leads,
      cpl: leads ? Math.round(spend / leads) : 0,
      problems: f.filter((a) => a.verdict === "drain" || a.verdict === "fatigued" || (a.issues || []).length > 0).length,
    };
  }, [filtered]);

  const activeFilters = [fCamp, fAdset, fStatus, fVerdict, fIssue].filter((f) => f !== "all").length + (q ? 1 : 0);
  const clearAll = () => { setFCamp("all"); setFAdset("all"); setFStatus("all"); setFVerdict("all"); setFIssue("all"); setQ(""); };

  const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === "desc" ? "asc" : "desc" }));

  const analyze = (ad) => {
    setDrawer({ title: ad.name, subtitle: "Vision analysis · Claude sees the actual image + copy & metrics" });
    ai.start({ type: "creative", entity_id: ad.id });
  };

  return (
    <div className="space-y-5">
      <div className="bg-white border border-[#E5E7EB] rounded-xl p-4" data-testid="ads-filter-bar">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-[#0A0D14] mr-1"><FunnelSimple size={15} weight="bold" /> Filters</span>
          <FSelect value={fCamp} onChange={(v) => { setFCamp(v); setFAdset("all"); }} all="All campaigns" options={campaigns.map((c) => ({ v: c, l: c }))} width="w-52" testId="ads-filter-campaign" />
          <FSelect value={fAdset} onChange={setFAdset} all="All ad sets" options={adsets.map((a) => ({ v: a, l: a }))} width="w-48" testId="ads-filter-adset" />
          <FSelect value={fStatus} onChange={setFStatus} all="All statuses" options={[{ v: "active", l: "Active" }, { v: "paused", l: "Paused" }]} width="w-32" testId="ads-filter-status" />
          <FSelect value={fVerdict} onChange={setFVerdict} all="Any verdict"
            options={[{ v: "top", l: "🏆 Top performer" }, { v: "drain", l: "🔻 Budget drain" }, { v: "fatigued", l: "😴 Fatigued" }, { v: "ok", l: "OK" }]} width="w-40" testId="ads-filter-verdict" />
          <FSelect value={fIssue} onChange={setFIssue} all="Any delivery state"
            options={[{ v: "with", l: "⚠️ With delivery errors" }, { v: "none", l: "✓ No errors" }]} width="w-44" testId="ads-filter-issue" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search ad, headline, campaign…"
            data-testid="ads-search-input" className="h-9 w-56 text-xs bg-white" />
          {activeFilters > 0 && (
            <button onClick={clearAll} className="inline-flex items-center gap-1 rounded-full bg-[#F3F4F6] text-[11px] font-bold text-[#525866] px-3 py-1.5 hover:bg-[#e7e9ee]" data-testid="ads-filter-clear">
              <X size={11} weight="bold" /> Clear ({activeFilters})
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="ads-stat-cards">
        <Stat icon={ImageSquare} label="Ads in view" value={stats.total.toLocaleString("en-IN")} sub="matching filters" testId="ads-stat-total" />
        <Stat icon={Pulse} label="Active" value={stats.active.toLocaleString("en-IN")} sub="currently delivering" testId="ads-stat-active" />
        <Stat icon={CurrencyInr} label="Spend" value={inr(stats.spend)} sub="last 14 days" accent testId="ads-stat-spend" />
        <Stat icon={UsersThree} label="Leads" value={stats.leads.toLocaleString("en-IN")} sub="last 14 days" testId="ads-stat-leads" />
        <Stat icon={Fire} label="Avg CPL" value={stats.cpl ? inr(stats.cpl) : "—"} sub="spend ÷ leads in view" accent testId="ads-stat-cpl" />
        <Stat icon={WarningCircle} label="Problem ads" value={stats.problems.toLocaleString("en-IN")} sub="drains + fatigued + errors" testId="ads-stat-problems" />
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-x-auto">
        {!sorted ? (
          <div className="p-6 space-y-3">{[...Array(8)].map((_, i) => <Skeleton key={i} className="h-11 rounded-md" />)}</div>
        ) : (
          <Table className="min-w-[1250px]">
            <TableHeader>
              <TableRow className="border-b border-[#E5E7EB] hover:bg-transparent">
                <TableHead className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8a90a0]">Ad</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8a90a0]">Status</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8a90a0]">
                  <span className="inline-flex items-center gap-1">Delivery
                    <TooltipProvider delayDuration={100}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button data-testid="ads-delivery-info" className="text-[#8a90a0] hover:text-[#0052FF]" aria-label="Delivery issues info"><Info size={12} weight="bold" /></button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="max-w-[270px] bg-[#0A0D14] text-white p-3 normal-case tracking-normal">
                          <p className="text-[11px] leading-relaxed">Meta's own delivery diagnostics for this ad, synced live from Ads Manager. Hover a red badge to read the full error. "—" means Meta reports no delivery issue.</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </span>
                </TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8a90a0]">Campaign</TableHead>
                {NUMERIC_COLS.map((c) => (
                  <TableHead key={c.key} className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8a90a0]">
                    <button onClick={() => toggleSort(c.key)} data-testid={`ads-sort-${c.key}`}
                      className="inline-flex items-center gap-0.5 uppercase tracking-[0.15em] font-bold hover:text-[#0A0D14] transition-colors">
                      {c.label}
                      {sort.key === c.key && (sort.dir === "desc" ? <CaretDown size={10} weight="bold" /> : <CaretUp size={10} weight="bold" />)}
                    </button>
                  </TableHead>
                ))}
                <TableHead className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8a90a0]">
                  <span className="inline-flex items-center gap-1">Meta Quality
                    <TooltipProvider delayDuration={100}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button data-testid="ads-quality-info" className="text-[#8a90a0] hover:text-[#0052FF]" aria-label="Meta quality ranking info"><Info size={12} weight="bold" /></button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="max-w-[260px] bg-[#0A0D14] text-white p-3 normal-case tracking-normal">
                          <p className="text-[11px] leading-relaxed">Facebook's own diagnostic: how this ad's perceived quality ranks against ads competing for the same audience. Shown only when the ad has ~500+ impressions.</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </span>
                </TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8a90a0]">
                  <span className="inline-flex items-center gap-1">Verdict
                    <TooltipProvider delayDuration={100}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button data-testid="ads-verdict-info" className="text-[#8a90a0] hover:text-[#0052FF]" aria-label="Verdict rules"><Info size={12} weight="bold" /></button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="max-w-[290px] bg-[#0A0D14] text-white p-3 normal-case tracking-normal">
                          <p className="font-bold mb-1.5">Audit verdict rules (last 14 days)</p>
                          <ul className="space-y-1 text-[11px] leading-relaxed">
                            <li>• <b>Budget drain</b> — spent ₹1,000+ with 0 leads</li>
                            <li>• <b>Top performer</b> — 3+ leads at CPL ≤ 70% of your median CPL ({medianCpl ? inr(medianCpl) : "—"})</li>
                            <li>• <b>Fatigued</b> — frequency ≥ 3.5 or Meta quality "Below average"</li>
                            <li>• <b>OK</b> — none of the above</li>
                          </ul>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </span>
                </TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8a90a0]">AI</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.length === 0 ? (
                <TableRow><TableCell colSpan={15} className="text-center text-xs text-[#8a90a0] py-8" data-testid="ads-empty">No ads match these filters. If the workspace was just re-connected, run a sync from the Connect page first.</TableCell></TableRow>
              ) : sorted.map((a) => {
                const v = VERDICTS[a.verdict];
                return (
                  <TableRow key={a.id} data-testid={`ad-row-${a.id}`} className="border-b border-[#E5E7EB]/40 hover:bg-[#FAFAFB]">
                    <TableCell>
                      <button onClick={() => setPreview(a)} data-testid={`ad-preview-${a.id}`} className="flex items-center gap-3 text-left w-full max-w-[230px] group">
                        <span className="h-10 w-10 shrink-0 rounded-md overflow-hidden bg-[#F3F4F6] border border-[#E5E7EB]">
                          {a.image_url ? <img src={a.image_url} alt="" className="h-full w-full object-cover" loading="lazy" /> : <ImageSquare size={16} className="m-auto mt-3 text-[#c3c7d1]" />}
                        </span>
                        <span className="min-w-0">
                          <span className="block text-xs font-bold text-[#0A0D14] truncate group-hover:text-[#0052FF] transition-colors" title={a.name}>{a.name}</span>
                          <span className="block text-[10px] text-[#8a90a0] truncate" title={a.adset_name}>{a.adset_name || "—"}</span>
                        </span>
                      </button>
                    </TableCell>
                    <TableCell>
                      <Badge className={`rounded-full font-bold text-[10px] uppercase border ${a.status === "active" ? "bg-success/10 text-[#00953e] border-success/25" : "bg-[#F3F4F6] text-[#8a90a0] border-[#E5E7EB]"}`}>{a.status}</Badge>
                    </TableCell>
                    <TableCell>
                      {(a.issues || []).length ? (
                        <TooltipProvider delayDuration={100}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold bg-red-50 text-red-500 border-red-200 cursor-help whitespace-nowrap" data-testid={`ad-issue-${a.id}`}>
                                <WarningCircle size={11} weight="fill" /> {issueLabel(a.issues[0].summary)}{a.issues.length > 1 ? ` +${a.issues.length - 1}` : ""}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" className="max-w-[320px] bg-[#0A0D14] text-white p-3 normal-case tracking-normal">
                              {a.issues.map((i, idx) => (
                                <div key={idx} className={idx ? "mt-2 pt-2 border-t border-white/15" : ""}>
                                  <p className="font-bold text-[11px]">{i.summary}</p>
                                  <p className="text-[11px] text-white/70 leading-relaxed mt-0.5">{i.message}</p>
                                </div>
                              ))}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ) : <span className="text-[11px] text-[#c3c7d1]">—</span>}
                    </TableCell>
                    <TableCell><div className="max-w-[170px] text-xs text-[#525866] truncate" title={a.campaign_name}>{a.campaign_name}</div></TableCell>
                    <TableCell className="font-mono text-xs font-bold text-[#0A0D14]">{a.spend ? inr(a.spend) : "—"}</TableCell>
                    <TableCell className="font-mono text-xs text-[#525866]">{compact(a.impressions)}</TableCell>
                    <TableCell className="font-mono text-xs text-[#525866]">{a.cpm ? inr(a.cpm) : "—"}</TableCell>
                    <TableCell className="font-mono text-xs text-[#525866]">{a.clicks.toLocaleString("en-IN")}</TableCell>
                    <TableCell className="font-mono text-xs text-[#525866]">{a.ctr}%</TableCell>
                    <TableCell className="font-mono text-xs font-bold text-[#0A0D14]">{a.leads}</TableCell>
                    <TableCell className="font-mono text-xs font-bold text-[#0A0D14]">{a.cpl ? inr(a.cpl) : "—"}</TableCell>
                    <TableCell className="font-mono text-xs text-[#525866]">{a.frequency || "—"}</TableCell>
                    <TableCell><RankBadge r={a.quality_ranking} /></TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold ${v.cls}`} data-testid={`ad-verdict-${a.id}`}>
                        <v.icon size={11} weight="fill" /> {v.label}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Button onClick={() => analyze(a)} data-testid={`analyze-creative-${a.id}`} variant="outline" size="sm"
                        className="rounded-full border-ai/30 text-ai hover:bg-ai/5 hover:text-ai font-bold text-[10px] h-7 px-2.5">
                        <Sparkle size={11} weight="fill" className="mr-1" /> Audit
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-md" data-testid="ad-preview-dialog">
          {preview && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-base font-extrabold">{preview.name}</DialogTitle>
                <DialogDescription className="text-[11px]">{preview.campaign_name} · {preview.adset_name || "—"}</DialogDescription>
              </DialogHeader>
              {preview.image_url && (
                <div className="rounded-lg overflow-hidden border border-[#E5E7EB] bg-[#F3F4F6] max-h-72">
                  <img src={preview.image_url} alt={preview.headline} className="w-full h-full object-contain max-h-72" />
                </div>
              )}
              <div>
                <div className="font-heading text-sm font-extrabold text-[#0A0D14]">{preview.headline}</div>
                <p className="text-xs text-[#525866] leading-relaxed mt-1.5 whitespace-pre-line max-h-36 overflow-y-auto">{preview.primary_text || "—"}</p>
              </div>
              <Button onClick={() => { setPreview(null); analyze(preview); }} data-testid="ad-preview-analyze"
                className="w-full rounded-full bg-ai hover:bg-[#5457e0] text-white font-bold text-xs h-10">
                <Sparkle size={14} weight="fill" className="mr-1.5" /> Audit with AI Vision
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>

      <AIDrawer open={!!drawer} onClose={() => { setDrawer(null); ai.reset(); }} title={drawer?.title} subtitle={drawer?.subtitle} text={ai.text} running={ai.running} error={ai.error} />
    </div>
  );
}
